import Dispatcher from "Dispatcher";
import UserActions from "./UserActions";
import userStore from "data/userStore";
import StereotypesActions from "./StereotypesActions";
import SuperRecordActions from "./SuperRecordActions";
import Services from "lib/Services";
import SuperRecordListActions from "./SuperRecordListActions";
import SearchResultActions from "./SearchResultActions";
import FormActions from "./meta/FormActions";
import DataDefActions from "./meta/DataDefActions";
import AbstractionAiActions from "./meta/AbstractionAiActions";
import AboutActions from "./meta/AboutActions";
import FileUploadActions from "./FileUploadActions";
import ajaxHelper from "./util/ajaxHelper";
import MappingActions from "data/MappingActions";
import promisify from "lib/promisify";
import { ADT_EPISODE_KEY } from "data/AdtStore";
import AdtActions from "data/AdtActions";
import { HEMO_MESSAGE_KEY } from "data/HemoStore";
import HemoActions from "data/HemoActions";
import { ECMOEMR_MESSAGE_KEY } from "data/EcmoEmrStore";
import EcmoEmrActions from "data/EcmoEmrActions";
import ClientId from "data/ClientId";
import GxepActions from "data/GxepActions";
import DefineActions from "jsmodel/DefineActions";
import LiveMap from "data/util/LiveMap";
import EntityName from "data/util/EntityName";
import StereotypesStore from "data/StereotypesStore";
import entityMetaStore from "data/meta/entityMetaStore";
import _or_munge_info from "jsmodel/_or_munge_info";
import MappingInfoStore from "data/meta/MappingInfoStore";
import ExportParamActions from "./ExportParamActions";

// These functions return promises, which are tracked through the LoadObjects to
// facilitate migration of non-reactive flows (of which there are a lot). New
// code should NOT use the promises returned from these methods (directly or
// via LoadObject).

const modelAwarenessPromises = new LiveMap(orgAndKey => {
    const [org, key] = orgAndKey.split(":");
    const eks = EntityName.is(key)
        ? [key]
        : StereotypesStore.getStereotypeByKey(key)
            .implementations
            .map(i => i.key);
    if (entityMetaStore.hasInfo(eks)) {
        // sweet!
        return Promise.resolve();
    }
    return promisify(Services.loadModelJson(org, key)).then(data => {
        data.choices && data.choices.forEach(def =>
            Dispatcher.dispatch({
                type: DefineActions.CHOICE,
                key: def.key,
                def,
            }));
        data.tables.forEach(def =>
            Dispatcher.dispatch({
                type: DefineActions.TABLE,
                key: def.key,
                def,
            }));
        data.entities.forEach(def =>
            Dispatcher.dispatch({
                type: DefineActions.ENTITY,
                key: def.key,
                def: _or_munge_info({INFO: def}),
            }));
        // do NOT pass data through!
    });
});

const implementationAwarenessPromises = new LiveMap(orgAndImplKey => {
    const [org, implKey] = orgAndImplKey.split(":");
    const tuples = StereotypesStore
        .getDataEntryStereotypes()
        .filter(s => s.magicRoot)
        .map(s => {
            const si = s.implementations
                .find(it => it.implementationKey === implKey);
            return [s, si];
        })
        .filter(([s, si]) => si != null)
        .map(([s, si]) => [s, si, entityMetaStore.hasInfo(si.key)]);
    if (tuples.some(([s, si, loaded]) => loaded)) {
        // sweet!
        return Promise.resolve();
    }
    // don't have awareness; this stereotype can get it for us
    return modelAwarenessPromises.get(org + ":" + tuples[0][0].key);
});

const mappingAwarenessPromises = new LiveMap(orgTargetSource => {
    const [org, targetKey, sourceKey] = orgTargetSource.split(":");
    if (MappingInfoStore.hasInfo(targetKey, sourceKey)) {
        // sweet!
        return Promise.resolve();
    }
    return promisify(Services.loadMappingJson(org, targetKey, sourceKey))
        .then(data => {
            data.mappings.forEach(def =>
                Dispatcher.dispatch({
                    type: DefineActions.MAPPING,
                    def: {INFO: def},
                }));
            // do NOT pass data through!
        });
});

const DataManager = {
    loadVersionInfo: () =>
        ajaxHelper(
            Services.loadVersion(),
            {
                type: AboutActions.LOADED_VERSION_INFO,
            },
            {
                type: AboutActions.LOAD_VERSION_INFO_ERROR,
            },
        ),

    loadDataDefinition: (entityKey, fieldKey, org) =>
        ajaxHelper(
            Services.loadFieldData(entityKey, fieldKey, org),
            {
                type: DataDefActions.LOADED,
                entityKey,
                fieldKey,
            },
            {
                type: DataDefActions.LOAD_ERROR,
                entityKey,
                fieldKey,
            },
        ),

    loadAbstractionAi: (org, entityKey, fieldKey, payload) =>
        ajaxHelper(
            Services.loadAbstractionAi(org, entityKey, fieldKey, payload),
            {
                type: AbstractionAiActions.LOADED,
                entityKey,
                fieldKey
            },
            {
                type: AbstractionAiActions.LOAD_ERROR,
                entityKey,
                fieldKey
            },
        ),

    loadFormDescriptor: (key, org) =>
        ajaxHelper(
            Services.loadEntityFormDescriptor(key, org),
            {
                type: FormActions.LOADED,
                key,
            },
            {
                type: FormActions.LOAD_ERROR,
                key,
            }
        ),

    search: (key, query, order, pageSize, page, onlyEnabledRecords) =>
        ajaxHelper(
            Services.searchEntity(query, order, key, pageSize, page, onlyEnabledRecords),
            {
                type: SearchResultActions.SEARCH_RESULT,
                query,
                order,
                key,
                pageSize,
                page,
                onlyEnabledRecords
            },
            {
                type: SearchResultActions.SEARCH_ERROR,
                query,
                order,
                key,
                pageSize,
                page,
                onlyEnabledRecords
            }
        ),

    createSuperrecord: (key, id, data, org, initiatingContext) =>
        ajaxHelper(
            Services.createEntityData(key, data, org),
            {
                type: SuperRecordActions.CREATED,
                key,
                id,
                initiatingContext,
            },
            {
                type: SuperRecordActions.CREATE_ERROR,
                key,
                id,
                initiatingContext,
            },
        ),

    loadSuperrecord: (key, id, org) =>
        ajaxHelper(
            Services.loadEntityData(key, id, org),
            {
                type: SuperRecordActions.LOADED,
                key,
                id,
            },
            error => ({
                type: error.status === 404
                    ? SuperRecordActions.DELETED
                    : SuperRecordActions.LOAD_ERROR,
                key,
                id,
                error,
            }),
        ),

    loadSuperrecordList: (key, org) =>
        ajaxHelper(
            Services.getByStereotype(org, key),
            {
                type: SuperRecordListActions.LOADED,
                key,
            },
            {
                type: SuperRecordListActions.LOAD_ERROR,
                key,
            },
        ),

    updateSuperrecord: (key, id, data, org, initiatingContext) =>
        ajaxHelper(
            Services.updateEntityData(key, id, data, org),
            {
                type: SuperRecordActions.UPDATED,
                key,
                id,
                initiatingContext,
            },
            {
                type: SuperRecordActions.UPDATE_ERROR,
                key,
                id,
                initiatingContext,
            },
        ),

    mergeSuperrecord: (key, targetId, sourceId, data, org, initiatingContext) =>
        ajaxHelper(
            Services.mergeEntityData(key, targetId, sourceId, data, org),
            {
                // TODO: we use SuperRecordActions.UPDATED because many stores
                // rely on this action to update a SR in many places. However
                // it would probably be better to create a
                // MergeActions.GLOBAL_MERGE_COMPLETE for code readability at
                // some point.
                type: SuperRecordActions.UPDATED,
                key,
                id: targetId,
                initiatingContext,
            },
            {
                type: SuperRecordActions.UPDATE_ERROR,
                key,
                id: targetId,
                initiatingContext,
            },
        ),

    deleteSuperrecord: (key, id, org, initiatingContext) =>
        ajaxHelper(
            ClientId.is(id)
                ? Promise.resolve()
                : Services.deleteEntityData(key, id, org),
            ignored => ({
                type: SuperRecordActions.DELETED,
                key,
                id,
                initiatingContext,
            }),
            {
                type: SuperRecordActions.DELETE_ERROR,
                key,
                id,
                initiatingContext,
            }
        ),

    loadUser: () =>
        ajaxHelper(
            Services.loadUser(),
            user => ({
                type: UserActions.LOADED,
                user,
            }),
            {
                type: UserActions.LOAD_ERROR,
            }
        ),

    updateLicensePreference: (impl, pref, value) =>
        ajaxHelper(
            Services.saveLicensePreference(
                userStore.getActiveOrgKey(),
                impl,
                pref,
                value,
            ),
            newValue => ({
                type: UserActions.LICENSE_PREFERENCE_UPDATED,
                impl,
                pref,
                newValue,
            }),
            error => ({
                type: UserActions.LICENSE_PREFERENCE_UPDATE_ERROR,
                impl,
                pref,
                error,
            }),
        ),

    updateOrgUserPreference: (pref, value) =>
        ajaxHelper(
            Services.saveOrgUserPreference(
                userStore.getActiveOrgKey(),
                pref,
                value,
            ),
            newValue => ({
                type: UserActions.ORG_USER_PREFERENCE_UPDATED,
                pref,
                newValue,
            }),
            error => ({
                type: UserActions.ORG_USER_PREFERENCE_UPDATE_ERROR,
                pref,
                error,
            }),
        ),

    loadExportParams: org =>
        ajaxHelper(
            Services.loadExportParams(org),
            {
                type: ExportParamActions.LOADED,
            },
            {
                type: ExportParamActions.LOAD_ERROR,
            },
        ),

    createExportParams: (org, data) =>
        ajaxHelper(
            Services.createExportParams(org, data),
            {
                type: ExportParamActions.CREATED
            },
            {
                type: ExportParamActions.CREATE_ERROR
            },
        ),

    updateExportParams: (org, data) =>
        ajaxHelper(
            Services.updateExportParams(org, data),
            {
                type: ExportParamActions.UPDATED
            },
            {
                type: ExportParamActions.UPDATE_ERROR
            },
        ),

    deleteExportParams: (org, id) =>
        ajaxHelper(
            Services.deleteExportParams(org, id),
            {
                type: ExportParamActions.DELETED
            },
            {
                type: ExportParamActions.DELETE_ERROR
            },
        ),

    loadGxepInfo: (org) =>
        ajaxHelper(
            Services.loadGxepInfo(org),
            {
                type: GxepActions.LOADED,
            },
            {
                type: GxepActions.LOAD_ERROR,
            }
        ),

    saveGxepInfo: (org, data) =>
        ajaxHelper(
            Services.saveGxepInfo(org, data),
            {
                type: GxepActions.SAVED,
                packet: data,
            },
            {
                type: GxepActions.SAVE_ERROR,
                packet: data,
            },
        ),

    loadStereotypes(org) {
        const p = ajaxHelper(
            Services.loadStereotypes(),
            {
                type: StereotypesActions.LOADED,
            }, {
                type: StereotypesActions.LOAD_ERROR,
            },
        );
        // don't chain p! The point is to do it in the background!
        p.then(ss => {
            const mrs = ss.stereotypes
                .filter(s => s.magicRoot);
            if (mrs.length === 1) {
                DataManager.promiseModelAwareness(org, mrs[0].key);
            }
            return ss;
        });
        return p;
    },

    promiseImplementationAwareness(org, implKey) {
        return implementationAwarenessPromises.get(`${org}:${implKey}`);
    },

    promiseModelAwareness(org, key) {
        return modelAwarenessPromises.get(`${org}:${key}`);
    },

    loadStereotype: key =>
        ajaxHelper(
            Services.loadStereotype(key),
            {
                type: StereotypesActions.STEREOTYPE_LOADED,
                key,
            }, {
                type: StereotypesActions.STEREOTYPE_LOAD_ERROR,
                key,
            },
        ),

    promiseMappingAwareness(org, targetKey, sourceKey) {
        return mappingAwarenessPromises.get(`${org}:${targetKey}:${sourceKey}`);
    },

    loadMappingModel: (org, targetKey, sourceKey) =>
        ajaxHelper(
            DataManager.promiseMappingAwareness(org, targetKey, sourceKey),
            ignored => ({
                type: MappingActions.MODEL_LOADED,
                targetKey,
                sourceKey,
            }),
            {
                type: MappingActions.MODEL_LOAD_ERROR,
                targetKey,
                sourceKey,
            },
        ),

    adtSearch: (org, query, interactive) =>
        DataManager.promiseModelAwareness(org, ADT_EPISODE_KEY)
            .then(() => ajaxHelper(
                Services.searchADT(org, query),
                {
                    type: AdtActions.SEARCH_RESULT,
                    query,
                    interactive,
                },
                {
                    type: AdtActions.SEARCH_ERROR,
                    query,
                    interactive,
                },
            )),

    hemoSearch: (org, query, interactive) =>
        DataManager.promiseModelAwareness(org, HEMO_MESSAGE_KEY)
            .then(() => ajaxHelper(
                Services.searchHemo(org, query),
                {
                    type: HemoActions.SEARCH_RESULT,
                    query,
                    interactive,
                },
                {
                    type: HemoActions.SEARCH_ERROR,
                    query,
                    interactive,
                },
            )),

    ecmoEmrSearch: (org, query, interactive) =>
        DataManager.promiseModelAwareness(org, ECMOEMR_MESSAGE_KEY)
            .then(() => ajaxHelper(
                Services.searchEcmoEmr(org, query),
                {
                    type: EcmoEmrActions.SEARCH_RESULT,
                    query,
                    interactive,
                },
                {
                    type: EcmoEmrActions.SEARCH_ERROR,
                    query,
                    interactive,
                },
            )),

    uploadFile(recordKey, recordId, fieldKey, file) {

        const buildFileUploadAction = (type, extra) => ({
            type: type,
            recordKey,
            recordId,
            fieldKey,
            file,
            ...extra,
        });
        const throttledProgress = _.throttle(
            (e) => Dispatcher.dispatch(
                buildFileUploadAction(
                    FileUploadActions.PROGRESS,
                    {
                        progress: e.lengthComputable ? ((e.loaded / e.total) * 100) : 0,
                    }
                )
            ),
            250
        );

        // The uploadFile service does not return a promise but instead uses a
        // callback function to obtain the promise once it is created.
        const uploadFilePromise = new Promise((resolve, reject) => {
            Services.uploadFile(
                file._org,
                file._impl,
                file._version,
                file._entity,
                file._record_id,
                file._field,
                file._data,
                (p) => { // callback function with the actual xhr promise
                    Dispatcher.dispatch(// dispatch that the upload has started and save the promise
                        buildFileUploadAction(
                            FileUploadActions.PROGRESS,
                            {
                                progress: 0,
                                promise: p // actual upload xhr promise so we can abort.
                            }
                        )
                    );
                    p.fail(reject);
                    resolve(p); // resolve with the xhr promise
                },
                (e) => { // progress callback
                    throttledProgress(e);
                }
            );
        });

        return ajaxHelper(
                uploadFilePromise,
                data => buildFileUploadAction(
                    FileUploadActions.COMPLETE,
                    {
                        value: data
                    }
                ),
                error => buildFileUploadAction(
                    FileUploadActions.Error,
                    {
                        error,
                    }
                )
            );
    }
};

export default DataManager;



// WEBPACK FOOTER //
// ./app/data/DataManager.js